/**
 * "This handle belongs to…" — the picker behind an unrecognised mention.
 *
 * Writes the handle onto the record you choose. That is the whole action: the
 * handle lives on the ambassador, partner, event or lead, which is the one
 * place it belongs, and every future tag from that account attributes itself.
 *
 * Deliberately not a free-text "who is this" note on the mention. A note would
 * answer this one post and nothing else; the handle answers every post the
 * account ever makes.
 */
import { useEffect, useMemo, useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { MATCH_LABEL } from '@/types/mentions'
import { assignHandle, loadHandleTargets, type HandleTarget } from './repository/handleTargets'
import './LinkHandleModal.css'

interface Props {
  handle: string
  onClose: () => void
  onLinked: () => void
}

export function LinkHandleModal({ handle, onClose, onLinked }: Props) {
  const [all, setAll] = useState<HandleTarget[]>([])
  const [term, setTerm] = useState('')
  const [saving, setSaving] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadHandleTargets()
      .then(setAll)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load records.'))
  }, [])

  // Matches anywhere in the name, not just the start. A handle rarely looks
  // like the company it belongs to, which is the whole reason this list needs
  // searching rather than scrolling.
  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    const hits = t ? all.filter((r) => r.name.toLowerCase().includes(t)) : all
    return hits.slice(0, 60)
  }, [all, term])

  const link = async (target: HandleTarget) => {
    setSaving(target.id)
    setError(null)
    try {
      await assignHandle(target, handle)
      onLinked()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.')
      setSaving('')
    }
  }

  return (
    <Modal
      title={`Who is @${handle}?`}
      onClose={onClose}
      width={560}
      footer={
        <button className="btn btn--ghost" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="linkh">
        <p className="linkh__lede">
          Choose the record this account belongs to — including an unverified lead, which is what
          most of the pipeline is. The handle is saved on that record, so every post it makes from
          now on is attributed, not just this one.
        </p>

        <input
          className="input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search ambassadors, partners, events and leads…"
          aria-label="Search records"
          autoFocus
        />

        {error && <div className="recform__error linkh__error">{error}</div>}

        <div className="linkh__list">
          {rows.map((r) => (
            <button
              className="linkh__row"
              key={`${r.kind}-${r.id}`}
              disabled={saving !== ''}
              onClick={() => void link(r)}
            >
              <span className="linkh__name">{r.name}</span>
              <span className={`pill linkh__kind linkh__kind--${r.kind}`}>
                {MATCH_LABEL[r.kind]}
              </span>
              {r.detail && <span className="muted linkh__detail">{r.detail}</span>}
              {/* Overwriting somebody else's handle is a real possibility and
                  the only warning you get is this one. */}
              {r.instagram && (
                <span className="linkh__has">already @{r.instagram} — will be replaced</span>
              )}
            </button>
          ))}
          {rows.length === 0 && (
            <div className="empty-note linkh__empty">
              {all.length === 0 ? 'Loading…' : 'No account matches that. Try part of the handle.'}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
